import React from 'react';
import {
  Button,
  ButtonProps,
  CircularProgress,
  alpha,
  useTheme,
  Box
} from '@mui/material';

interface ModernButtonProps extends ButtonProps {
  loading?: boolean;
  gradient?: boolean;
  glass?: boolean;
  icon?: React.ReactNode;
}

const ModernButton: React.FC<ModernButtonProps> = ({
  loading = false,
  gradient = false,
  glass = false,
  icon,
  children,
  ...props
}) => {
  const theme = useTheme();

  const getButtonStyles = () => {
    const baseStyles = {
      borderRadius: '12px',
      padding: '12px 24px',
      fontWeight: 600,
      fontSize: '15px',
      textTransform: 'none' as const,
      position: 'relative' as const,
      overflow: 'hidden' as const,
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      minHeight: '48px',
      '&:disabled': {
        opacity: 0.6,
      }
    };

    if (glass) {
      return {
        ...baseStyles,
        background: alpha(theme.palette.background.paper, 0.1),
        backdropFilter: 'blur(20px)',
        border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
        color: theme.palette.primary.main,
        '&:hover': {
          background: alpha(theme.palette.primary.main, 0.1),
          transform: 'translateY(-2px)',
          boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.3)}`,
        }
      };
    }

    if (gradient) {
      return {
        ...baseStyles,
        background: `linear-gradient(135deg,
          ${theme.palette.primary.main} 0%,
          ${theme.palette.primary.dark} 100%)`,
        color: theme.palette.primary.contrastText,
        border: 'none',
        '&:hover': {
          background: `linear-gradient(135deg,
            ${theme.palette.primary.dark} 0%,
            ${theme.palette.primary.main} 100%)`,
          transform: 'translateY(-2px)',
          boxShadow: `0 12px 40px ${alpha(theme.palette.primary.main, 0.4)}`,
        },
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: '-100%',
          width: '100%',
          height: '100%',
          background: `linear-gradient(90deg,
            transparent 0%,
            ${alpha('#fff', 0.2)} 50%,
            transparent 100%)`,
          transition: 'left 0.6s ease',
        },
        '&:hover::before': {
          left: '100%',
        }
      };
    }

    return {
      ...baseStyles,
      '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.2)}`,
      }
    };
  };

  return (
    <Button
      {...props}
      disabled={loading || props.disabled}
      sx={{
        ...getButtonStyles(),
        ...props.sx
      }}
    >
      <Box display="flex" alignItems="center" gap={1}>
        {loading && <CircularProgress size={20} color="inherit" />}
        {!loading && icon && (
          <Box component="span" sx={{ display: 'flex', fontSize: '18px' }}>
            {icon}
          </Box>
        )}
        {children}
      </Box>
    </Button>
  );
};

export default ModernButton;
